import {
  computeTurnMap,
  outlineFilterVisitors,
  type TurnInfo,
} from "./outline/tree-visitors";
import { flatTree } from "./transform/flatten";
import { isSentinelSpan } from "./transform/toolSentinels";
import { kDefaultExcludeEvents, type EventNode } from "./types";

// Tuck above the pin line, tuned by eye — the row's top anatomy doesn't
// reduce to any one measured height.
export const kTranscriptScrollPaddingStart = -20;

// The events the transcript hides by default, plus the span markers - dropped
// from the focus slice so the focused model renders next to its tool events
// (compact summary, not inline tool calls).
const kFocusExcludedEvents = new Set([
  ...kDefaultExcludeEvents,
  "span_begin",
  "span_end",
]);

/**
 * Turn map + ordered turn-anchor ids, shared by the transcript list and the
 * single-event page so their "turn N/M" numbering can't disagree. Pass the
 * *default* collapse state, not the user's live collapse, so collapsing a
 * region doesn't renumber turns below it. `anchorIdByTurn` maps every turn
 * number to its model anchor id even when live collapse hides it (while
 * `anchorIds` skips hidden turns).
 */
export function computeTranscriptTurns(
  eventNodes: EventNode[],
  flattenedNodes: EventNode[],
  collapsed: Record<string, boolean> | null
): {
  turnMap: Map<string, TurnInfo>;
  anchorIds: string[];
  anchorIdByTurn: Map<number, string>;
} {
  const outlineFiltered = flatTree(
    eventNodes,
    collapsed,
    outlineFilterVisitors()
  );
  const { turnMap, anchorIdByTurn } = computeTurnMap(
    outlineFiltered,
    flattenedNodes
  );
  return {
    turnMap,
    anchorIds: computeTurnAnchorIds(flattenedNodes, turnMap),
    anchorIdByTurn,
  };
}

/**
 * Ordered ids of the model events that open each visible turn — the j/k and
 * header-chevron targets; skips turns hidden in a collapsed region.
 */
export function computeTurnAnchorIds(
  flattenedNodes: EventNode[],
  turnMap: Map<string, TurnInfo>
): string[] {
  const ids: string[] = [];
  let last = 0;
  for (const node of flattenedNodes) {
    const turn = turnMap.get(node.id)?.turnNumber;
    if (turn !== undefined && turn > last) {
      ids.push(node.id);
      last = turn;
    }
  }
  return ids;
}

/**
 * Index into `anchorIds` for a 1-based turn number — NOT simply
 * `turnNumber - 1`: collapsed regions make anchors skip turns.
 */
export function anchorIndexForTurn(
  anchorIds: string[],
  turnMap: Map<string, TurnInfo>,
  turnNumber: number
): number {
  let index = 0;
  for (let i = 0; i < anchorIds.length; i++) {
    const id = anchorIds[i];
    const n = id !== undefined ? turnMap.get(id)?.turnNumber : undefined;
    if (n === undefined) continue;
    if (n > turnNumber) break;
    index = i;
  }
  return index;
}

/**
 * Anchor index for an arbitrary event id, or -1 ("above turn 1") for events
 * with no turn. Shared by every current-turn writer so they can't disagree
 * on what "unknown" means.
 */
export function anchorIndexForEvent(
  anchorIds: string[],
  turnMap: Map<string, TurnInfo>,
  eventId: string | null | undefined
): number {
  const turnNumber = eventId ? turnMap.get(eventId)?.turnNumber : undefined;
  return turnNumber === undefined
    ? -1
    : anchorIndexForTurn(anchorIds, turnMap, turnNumber);
}

/**
 * Lane name when the node opens a subagent scope (agent span, or a fork-style
 * `subtask` event), else undefined.
 */
function agentBoundaryName(node: EventNode): string | undefined {
  const event = node.event;
  if (event.event === "span_begin" && event.type === "agent") {
    return event.name || "agent";
  }
  if (event.event === "subtask") {
    return event.name || "subtask";
  }
  return undefined;
}

/**
 * Resolve an event to the nearest preceding model IN THE SAME AGENT LANE —
 * a plain document-order back-scan would land a main-lane tool that follows
 * a subagent span on the subagent's last turn instead of its own.
 */
export function resolveEventTurnAnchor(
  flattenedNodes: EventNode[],
  eventId: string
): string | undefined {
  // Lanes are identified by the innermost agent boundary's node id (null = main).
  const stack: Array<{ depth: number; laneId: string | null }> = [];
  const lastAnchorByLane = new Map<string | null, string>();
  // Depth of the sentinel span being scanned: its monitor calls are not turns.
  let sentinelDepth: number | undefined;
  for (const node of flattenedNodes) {
    while (stack.length > 0 && node.depth <= stack[stack.length - 1]!.depth) {
      stack.pop();
    }
    if (sentinelDepth !== undefined && node.depth <= sentinelDepth) {
      sentinelDepth = undefined;
    }
    const laneId = stack.length > 0 ? stack[stack.length - 1]!.laneId : null;
    const event = node.event;
    if (event.event === "model" && sentinelDepth === undefined) {
      lastAnchorByLane.set(laneId, node.id);
    }
    if (node.id === eventId) {
      return lastAnchorByLane.get(laneId);
    }
    if (agentBoundaryName(node) !== undefined) {
      stack.push({ depth: node.depth, laneId: node.id });
    }
    if (sentinelDepth === undefined && isSentinelSpan(node)) {
      sentinelDepth = node.depth;
    }
  }
  return undefined;
}

/** Events that close out a sample: they trail the last turn OUTSIDE every
 *  turn span, so depth-based slicing drops them. */
export const kSampleTerminalEvents = new Set([
  "error",
  "sample_limit",
  "score",
]);

/**
 * Focus-page nodes for `eventId`: its turn slice minus structural/noise
 * events. Takes the *unfiltered* flat list (`flatTree(eventNodes, null)`) —
 * filtering spans out first would drop the span_begin that ends a turn and
 * run the slice into later turns. `keep` retains structural nodes that host a
 * row of their own (e.g. a sentinel span standing in for its step).
 */
export function focusedTurnNodes(
  flat: EventNode[],
  eventId: string,
  keep?: (node: EventNode) => boolean
): EventNode[] {
  const slice = turnSlice(flat, eventId);
  // The transcript renders the sample's error/limit card right after the last
  // turn; keep it visible in the LAST turn's focus slice too (a focused tool
  // or a turn with later model turns is not "the end of the sample").
  const target = slice[0];
  if (!target) return [];
  const end = flat.indexOf(slice[slice.length - 1]!) + 1;
  const isLastTurn =
    target.event.event === "model" &&
    !flat.slice(end).some((n) => n.event.event === "model");
  const trailing = isLastTurn
    ? flat.slice(end).filter((n) => kSampleTerminalEvents.has(n.event.event))
    : [];
  return [...slice, ...trailing].filter(
    (n) => !kFocusExcludedEvents.has(n.event.event) || !!keep?.(n)
  );
}

/**
 * Flat-list slice for a focused event: a model's whole turn (up to the next
 * model at same-or-shallower depth), otherwise the event and its descendants.
 */
function turnSlice(flat: EventNode[], eventId: string): EventNode[] {
  const start = flat.findIndex((n) => n.id === eventId);
  const target = start === -1 ? undefined : flat[start];
  if (!target) return [];
  const depth = target.depth;
  const isModel = target.event.event === "model";
  let end = start + 1;
  while (end < flat.length) {
    const node = flat[end];
    if (!node) break;
    if (node.depth < depth) break;
    if (isModel) {
      if (node.depth === depth && node.event.event === "model") break;
    } else if (node.depth <= depth) {
      break;
    }
    end++;
  }
  return flat.slice(start, end);
}
